import * as React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { BookingStatusEnum, ListMyBookingsQuery } from '@graphql';
import { useSession } from '@hooks';

interface BookingSlotProps {
  booking: ListMyBookingsQuery['myBookings'][0];
}

function BookingSlot(props: Readonly<BookingSlotProps>) {
  const { booking } = props;
  const { account } = useSession();
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation('booking');

  let name = '';
  let avatarUrl: string | null = '';

  if (account?.__typename === 'Spot') {
    if (booking.client.__typename === 'BookingClient') {
      const { firstname, lastname } = booking.client;
      name = `${firstname} ${lastname ?? ''}`.trim();
    } else if (booking.client.__typename === 'Consumer') {
      const { firstname, lastname, deletedAt } = booking.client.user;
      name = deletedAt ? t(`user.deleted user`, { ns: 'account' }) : `${firstname} ${lastname}.`;
      avatarUrl = deletedAt ? null : (booking.client.avatar?.url ?? '');
    }
  } else {
    name = booking.spot.name;
    avatarUrl = booking.spot.avatar?.url ?? '';
  }

  const handleClick = () => {
    navigate(`/me/calendar/booking/${booking.id}/detail`, {
      preventScrollReset: true,
      state: { from: pathname },
    });
  };

  return (
    <Stack
      direction="row"
      onClick={handleClick}
      p={0}
      justifyContent="flex-start"
      divider={<Divider />}
      flex={1}
      sx={{
        borderRadius: theme.borderRadius,
        backgroundColor: theme.palette.grey[900],
        overflowY: 'scroll',
      }}
    >
      <Status status={booking.status} />
      <Stack
        direction="row"
        spacing={2}
        p={1}
        width="100%"
        height="100%"
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack spacing={1} justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar src={avatarUrl} sx={{ width: 40, height: 40 }}>
              {avatarUrl ? name[0] : ''}
            </Avatar>
            <Stack>
              <Typography variant="body2" fontWeight="bolder">
                {name}
              </Typography>
              <Typography variant="caption" fontSize=".8rem" color="textSecondary">
                {t('slot.booking for x pers at', {
                  count: booking.participants,
                  time: moment(booking.startTime).format('HH:mm'),
                })}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}

interface StatusProps {
  status: BookingStatusEnum;
}

function Status(props: Readonly<StatusProps>) {
  const { status } = props;
  const theme = useTheme();

  const statusColor = {
    [BookingStatusEnum.Accepted]: theme.palette.success.main,
    [BookingStatusEnum.Canceled]: theme.palette.error.main,
    [BookingStatusEnum.NoShow]: 'black',
    [BookingStatusEnum.Pending]: theme.palette.warning.main,
    [BookingStatusEnum.Rejected]: theme.palette.error.main,
  };

  return <Box width="5px" sx={{ backgroundColor: statusColor[status], borderRadius: '20px' }} />;
}

export default React.memo(BookingSlot);
